import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { ApiService } from 'src/app/services/api/api.service';
import { NotificationService } from 'src/app/services/notification.service';
import { environment } from 'src/environments/environment';
import * as Constants from "src/app/constants/constants";
import { FormGroup } from '@angular/forms';

@Component({
  selector: 'app-cards',
  templateUrl: './cards.component.html',
  styleUrls: ['./cards.component.scss']
})
export class CardsComponent {
  @Input() formGroup!: FormGroup;
  @Input() payer!: any;
  @Input() amount!: number;
  @Input() returnURL!: string;

  @Output() paymentSuccess = new EventEmitter<void>();
  @Output() paymentCompleted = new EventEmitter<void>();

  accessToken = signal<string>('');
  deviceDataCollectionURL = signal<string>('');
  reference = signal<string>('');
  stepUpURL = signal<string>('');
  deviceDetails = signal<any>(null);

  requiredFieldString: string = 'Required field';
  formErrors: { [key: string]: string } = {
    CardNumber: '',
    CVV: '',
    Expiry: '',
    CardName: '',
    Currency: ''
  };
  validationMessages: { [key: string]: {} } = {
    Currency: { required: this.requiredFieldString },
    CardNumber: { required: this.requiredFieldString },
    CVV: { required: this.requiredFieldString},
    Expiry: { required: this.requiredFieldString },
    CardName: { required: this.requiredFieldString },
  };


  constructor(
    private readonly notification: NotificationService,
    private readonly api: ApiService
  ) {}


  formControl(controlName: string) {
    return this.formGroup.get(controlName);
  }

  // Init payment; Returns reference number
  async createPaymentIntent() {
    try {
      this.notification.isLoading(true);
      const data = {
        Amount: this.amount,
        Currency: this.formControl('Currency')?.value,
        FirstName: this.payer.FirstName,
        LastName: this.payer.LastName,
        Address: '',
        Locality: '',
        PostalCode: '',
        Country: this.payer.Country || '',
        Email: this.payer.Email,
        Phone: this.payer.Phone,
        ReturnURL: `${environment.siteURL}/${this.returnURL}`
      };

      const response = await this.api.postRequest(
        environment.baseUrl + Constants.createCardPaymentIntentURL, data);

      if (response.Status === 1) {
        this.reference.set(response.Data.reference);
        await this.getCollectionsURL();
      } else {
        this.notification.viewToast('error', 'Cards Payment Error', 'Failed to create payment intent');
        this.notification.isLoading(false);
      }
      
    } catch (error) {
      console.error('Failed to create payment intent : ', error);
      this.notification.viewToast('error', 'An error occurred', 'Could not complete payment');
      this.notification.isLoading(false);
    }
  }

  // Updates access token and device collections Url
  async getCollectionsURL() {
    try {
      const data = {
        Reference: this.reference(),
        CCNumber: this.formControl('CardNumber')?.value, //Card number
        CVV: this.formControl('CVV')?.value, //CVV
        Expiry: this.formControl('Expiry')?.value, //Expiry date (MM/YY) or (MM/YYYY) or (MMYY)
        NameOnCard: this.formControl('CardName')?.value //Cardholder name
      };

      const response = await this.api.postRequest(
        environment.baseUrl + Constants.collectCardDeviceDetailsURL, data);

      if (response.Status === 1) {
        this.accessToken.set(response.Data.paToken.accessToken);
        this.deviceDataCollectionURL.set(response.Data.paToken.deviceDataCollectionUrl);
        await this.collectDeviceDetails();
      } else {
        this.notification.viewToast('error', 'Cards Payment Error', 'Collections URL not available');
        this.notification.isLoading(false);
      }
      
    } catch (error) {
      console.error('Failed to retrieve collections URL : ', error);
      this.notification.viewToast('error', 'An error occurred', 'Could not complete payment');
      this.notification.isLoading(false);
    }
  }

  // Browser-side
  async collectDeviceDetails() {
    try {
      const form = document.createElement("form");
      form.id = "deviceDataCollectionForm";
      form.method = "POST";
      form.action = this.deviceDataCollectionURL(); // From getCollectionsURL() response
      form.style.display = "none";
      form.target = "collectionIframe";

      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "JWT";
      input.value = this.accessToken(); // From getCollectionsURL() response
      input.id = "cardinal_collection_form_input";
      form.appendChild(input);

      const iframe = document.createElement("iframe");
      iframe.name = "collectionIframe";
      iframe.style.display = "none";
      iframe.id = "cardinal_collection_iframe";
      iframe.width = "10";
      iframe.height = "10";

      document.body.appendChild(form);
      document.body.appendChild(iframe);
      form.submit();

      window.addEventListener("message", (event) => {
        // Verify origin: 
        // Test: https://centinelapistag.cardinalcommerce.com
        // Prod: https://centinelapi.cardinalcommerce.com
        if (event.origin.includes("cardinalcommerce.com")) {
          this.deviceDetails.set(event.data);
          console.log("Device details collected:", this.deviceDetails());
          // Proceed to check status
          this.check3DSEnrollment();
        } else {
          this.notification.viewToast('error', 'Cards Error', 'Failed to retrieve device details');
          this.notification.isLoading(false);
        }
      });
    } catch (error) {
      console.error('Failed to collect device details ', error);
      this.notification.viewToast('error', 'An error occurred', 'Could not retrieve device details');
      this.notification.isLoading(false);
    }
  }

  async check3DSEnrollment() {
    try {
      const response = await this.api.getRequest(
        `${environment.baseUrl}${Constants.checkCardEnrollmentURL}?ref=${this.reference()}`);

      if (response.Status === 1) {
        if (response.Data?.action?.toLowerCase() === 'make_payment') {
          await this.processPayment();

        } else if (response.Data?.action?.toLowerCase() === 'authenticate') {
          this.accessToken.set(response.Data.accessToken);
          this.stepUpURL.set(response.Data.stepUpUrl);
          await this.renderStepUpAuth();

        } else { // failed
          this.notification.viewToast('error', 'Card Payment Failed', 'Please try another payment method');
        }
      } else {
        this.notification.viewToast('error', 'Cards Payment Error', 'Failed to complete payment');
        this.notification.isLoading(false);
      }
      
    } catch (error) {
      console.error('Failed to check 3DS Enrollment : ', error);
      this.notification.viewToast('error', 'An error occurred', 'Could not complete payment');
      this.notification.isLoading(false);
    }
  }

  // Browser-side
  async renderStepUpAuth() {
    try {
      const form = document.createElement("form");
      form.id = "stepUpForm";
      form.method = "POST";
      form.action = this.stepUpURL(); 
      
      // CRITICAL FIX: Force the form to submit in the EXACT SAME TAB.
      // This kills the multi-tab behavior.
      form.target = "_self"; 

      const jwtInput = document.createElement("input");
      jwtInput.type = "hidden";
      jwtInput.name = "JWT";
      jwtInput.value = this.accessToken(); 
      form.appendChild(jwtInput);

      const mdInput = document.createElement("input");
      mdInput.type = "hidden";
      mdInput.name = "MD";
      mdInput.value = this.reference(); 
      form.appendChild(mdInput);

      document.body.appendChild(form);
      form.submit();
      
    } catch (error) {
      console.error('Failed to render step up auth ', error);
      this.notification.viewToast('error', 'An error occurred', 'Could not complete payment');
      this.notification.isLoading(false);
    }
  }

  async processPayment() {
    try {
      const response = await this.api.getRequest(
        `${environment.baseUrl}${Constants.processCardPaymentURL}?ref=${this.reference()}` );

      if (response.Status === 1 && response.Data.status === 'COMPLETED') {
        this.notification.viewToast('success', 'Payment completed');
        // Emit success event to the parent component
        this.paymentSuccess.emit();
      } else {
        this.notification.viewToast('error', 'Payment failed', 'Please try another payment method');
      }
    } catch (error) {
      console.error('Failed to process payment : ', error);
      this.notification.viewToast('error', 'An error occurred', 'Could not complete payment');
    } finally {
      this.notification.isLoading(false);
    }
  }
}
